import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Safety net for the well-known Radix UI bug where `body { pointer-events: none }`
 * stays applied after a modal overlay (Dialog / AlertDialog / Select / DropdownMenu)
 * unmounts before its close cleanup runs (typically on a route change).
 *
 * On every route change — and also on tab visibility change — verify that no Radix
 * modal is actually open. If none are open but the body is still locked, release it.
 */
export function useBodyPointerEventsGuard() {
  const location = useLocation();

  useEffect(() => {
    const release = () => {
      if (typeof document === "undefined") return;
      if (document.body.style.pointerEvents !== "none") return;
      const stillOpen = document.querySelector(
        '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"], [data-state="open"][role="menu"], [data-state="open"][role="listbox"]',
      );
      if (!stillOpen) {
        document.body.style.pointerEvents = "";
      }
    };

    // Run after the current paint so Radix has a chance to clean up first.
    const raf = requestAnimationFrame(release);
    const timeout = window.setTimeout(release, 250);

    document.addEventListener("visibilitychange", release);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timeout);
      document.removeEventListener("visibilitychange", release);
    };
  }, [location.pathname]);
}
